<template>
  <div
    ref='rootRef'
    class='storages-show'
    :style='{
      top: isMobile ? `${popupPositionY}px` : undefined
    }'
  >
    <div v-if='isMobile && storage.photoUrl' class='photo'>
      <img :src='storage.photoUrl'>
    </div>
    <div class='headline-address'>
      <div class='headline'>
        <ChevronLeftIcon
          v-if='!isMobile'
          class='back'
          @click='emit("back:click")'
        />
        <a :href='storage.url'>
          {{ storage.name }}
        </a>
        <button
          v-if='isMobile'
          class='close'
          @click='emit("back:click")'
        >
          <CloseIcon />
        </button>
      </div>
      <div v-if='isMobile' class='address'>{{ storage.address }}</div>
    </div>

    <div
      v-if='boxes?.length || !isMobile'
      class='filter'
    >
      <LabelButton
        v-for='(sizeFilter, index) in storageSizeFilters'
        :key='index'
        :is-active='sizeFilter.isChecked'
        :is-disabled='sizeFilter.isDisabled'
        @click='toggleSizeFilter(sizeFilter.size)'
      >
        <span class='label'>{{ tpSizes(`${sizeFilter.size}.title`) }}</span>
        <span class='hint'>{{ tpSizes(`${sizeFilter.size}.subtitle`) }}</span>
      </LabelButton>
    </div>

    <Scrollable :is-active='!isMobile'>
      <Loader
        v-if='!boxes'
        :is-mobile='isMobile'
      />
      <template v-else-if='!boxes.length'>
        <StorageLeadForm
          class='lead-form'
          :i18n-key='storage.isSoon ? "is_soon" : "no_available_boxes"'
          :submit-url='leadProps.submitUrl'
          :lead-type='leadProps.leadType'
          :country-codes='leadProps.countryCodes'
        />
      </template>
      <template v-else>
        <TransitionGroup
          tag='div'
          :class='{ "is-animations": isAnimations }'
          :duration='isAnimations ? ANIMATIONS_DURATION : 0'
        >
          <Box
            v-for='box in firstTwelveBoxes'
            :key='box.id'
            class='box'
            v-bind='box'
          />
        </TransitionGroup>

        <div v-if='remainingBoxes.length'>
          <Box
            v-for='box in remainingBoxes'
            :key='box.id'
            v-bind='box'
          />
        </div>
      </template>
    </Scrollable>
  </div>
</template>

<script>
export default {
  name: 'StoragesShow'
};
</script>

<script setup>
import {
  ref,
  computed,
  nextTick,
  watch,
  onBeforeMount,
  onMounted
} from 'vue';
import delay from 'delay';

import { useMobileDetect } from '../../composables/mobile_detect';

import LabelButton from '../../components/label_button';
import Scrollable from '../scrollable';
import Loader from './loader';
import Box from './box';
import StorageLeadForm from '../../storage_schema_boxes/lead_form';

import CloseIcon from '@/../images/vue/storages_map-v4/storages_show/index/x-close.svg?inline';
import ChevronLeftIcon from '@/../images/vue/storages_map-v4/storages_show/index/chevron-left.svg?inline';
import graphqlFetch from '@/helpers/graphql_fetch';

import { t, prepared } from '@/helpers/i18n';

const ANIMATED_BOXES_THRESHOLD = 6;
const ANIMATIONS_DURATION = 500;

const props = defineProps({
  storage: { type: Object, required: true },
  boxes: { type: Array, required: false, default: undefined },
  allBoxSizes: { type: Array, required: true },
  containerNode: { type: Object, required: false, default: undefined },
  graphqlUrl: { type: String, required: true },
  leadProps: { type: Object, required: true }
});

const emit = defineEmits(['back:click', 'boxes:fetch']);
const tp = prepared('frontend.storages_map-v4.storages_show.index');
const tpSizes = prepared('frontend.storage_schema_boxes.index.sizes');

const { isMobile } = useMobileDetect();

const rootRef = ref(null);
const popupPositionY = ref(0);

const activeSizes = ref([]);
const isAnimations = ref(true);
const storageSizeFilters = computed(() => (
  props.allBoxSizes.map(size => ({
    size,
    isChecked: activeSizes.value.includes(size),
    isDisabled: !props.boxes || !props.boxes.some(box => box.size === size)
  }))
));
const filteredBoxes = computed(() => {
  if (!activeSizes.value.length) {
    return props.boxes;
  }

  return props.boxes?.filter(box => activeSizes.value.includes(box.size));
});

const animatedBoxesCount = computed(() => {
  if (!filteredBoxes.value) return 0;

  return filteredBoxes.value.length > ANIMATED_BOXES_THRESHOLD * 2 ?
    ANIMATED_BOXES_THRESHOLD :
    filteredBoxes.value.length;
});

const firstTwelveBoxes = computed(() =>
  filteredBoxes.value ? filteredBoxes.value.slice(0, animatedBoxesCount.value) : []
);

const remainingBoxes = computed(() =>
  filteredBoxes.value ? filteredBoxes.value.slice(animatedBoxesCount.value) : []
);

onBeforeMount(async () => {
  if (isMobile && props.containerNode) {
    calculatePopupPositionY(props.containerNode);
  }

  if (props.boxes) { return; }

  fetchBoxes();
});

onMounted(() => {
  if (isMobile && !props.containerNode) {
    calculatePopupPositionY(rootRef.value);
  }
});

watch(() => props.storage.id, async () => {
  const prevStorageId = props.storage.id;

  // нужно выключить анимации, чтобы боксы не прыгали,
  // если мы переключились между складами
  isAnimations.value = false;

  if (props.boxes) {
    await delay(ANIMATIONS_DURATION * 1.25);
  } else {
    await fetchBoxes();
  }

  if (props.storage.id === prevStorageId) {
    isAnimations.value = true;
  }
});

async function fetchBoxes() {
  const storageId = props.storage.id;
  const [{ boxes }] = await Promise.all([
    graphqlFetch(`
      query {
        boxes:availableBoxes(storageId: ${storageId}) {
          id
          name
          size
          capacity:capacityValue
          discounts
          squareText:space
          dimensionsText:dimensions
          capacityText:capacity
          priceWoDiscount:minPrice
          priceWithDiscount:minPriceWithDiscount
          url
          orderUrl
        }
      }
    `, props.graphqlUrl),
    delay(ANIMATIONS_DURATION * 1.25) // не загружаем боксы быстрее, чем время анимации
  ]);
  emit('boxes:fetch', { storageId, boxes });
}

function toggleSizeFilter(size) {
  const sizeFilter = storageSizeFilters.value.find(f => f.size === size);
  if (sizeFilter.isDisabled) return;

  if (activeSizes.value.includes(size)) {
    activeSizes.value = activeSizes.value.filter(s => s !== size);
  } else {
    activeSizes.value.push(size);
  }
}

function calculatePopupPositionY(parentNode) {
  const screenHeight = window.innerHeight;
  const targetPosition = (screenHeight * 2) / 3;
  const parentRect = parentNode.getBoundingClientRect();

  const offsetY = targetPosition - parentRect.top;
  const offsetHeight = parentNode.parentNode.offsetHeight;

  popupPositionY.value = Math.round(
    offsetY > offsetHeight * 0.9 ?
      offsetHeight * 0.9 :
      offsetY
  );
}
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/mixins/typography'
@import 'app/packs/stylesheets/mixins/fonts'
@import 'app/packs/stylesheets/mixins/link'
@import 'app/packs/stylesheets/mixins/ltr_rtl'
@import 'app/packs/stylesheets/mixins/button_v4'
@import 'app/packs/stylesheets/variables'

.storages-show
  +lte_ipad
    background: #fff
    border-radius: rem4(24px)
    box-shadow: rem4(-4px) rem4(8px) rem4(20px) 0 rgba(0, 0, 0, 0.10)
    gap: rem4(24px)
    position: absolute
    width: 100%
    z-index: $z-index-sticky_area + 1

.photo
  +lte_ipad
    position: relative

    img
      border-radius: rem4(16px)
      height: rem4(185px)
      object-fit: cover
      width: 100%

    button
      +button_v4('secondary', 'sm', $is_empty: true)
      border: 1px solid #fff
      position: absolute
      right: rem4(8px)
      top: rem4(8px)

.headline-address
  +lte_ipad
    display: flex
    flex-direction: column
    gap: rem4(8px)
    margin-bottom: rem4(8px)
    order: -1

  .headline
    align-items: center
    display: flex
    margin: 0

    +lte_ipad
      +p1(700)
      gap: rem4(32px)
      justify-content: space-between

    +gte_laptop
      +h4
      gap: 16px

    a
      text-decoration: none
      transition: color var(--transition-duration)

  .address
    +p4

+lte_ipad
  .close
    +button_v4('secondary', 'sm')
    border: 1px solid #fff
    height: rem4(32px) !important
    width: rem4(32px) !important

    svg
      flex-shrink: 0
      height: rem4(18px)
      width: rem4(18px)

+gte_laptop
  .back
    color: #667085
    cursor: pointer
    flex-shrink: 0
    transition: color var(--transition-duration)

    &:hover
      color: var(--gray-600)

    &:active
      color: var(--gray-700)

.filter
  display: flex

  +lte_ipad
    gap: rem4(8px)
    margin-bottom: rem4(-12px)
    overflow-x: scroll
    padding-bottom: rem4(12px)

  +gte_laptop
    gap: 8px

.url
  +p4
  +link_color
  +margin-left_ltr(auto)
  white-space: nowrap

::v-deep(.lead-form)
  +lte_ipad
    padding-bottom: rem4(80px)
    padding-top: rem4(23px)

  +gte_laptop
    flex-grow: 1
    justify-content: center
    padding-bottom: 70px

// animations
.is-animations
  .box
    &.v-move, /* apply transition to moving elements */
    &.v-enter-active,
    &.v-leave-active
      transition: all 500ms
      width: 100%

    /* ensure leaving items are taken out of layout flow so that moving
       animations can be calculated correctly. */
    &.v-leave-active
      position: absolute

    &.v-enter-from,
    &.v-leave-to
      opacity: 0

      +ltr
        transform: translateX(#{rem(45px)})

      +rtl
        transform: translateX(#{rem(-45px)})
</style>
